import { lazy, Suspense, useState } from 'react';
import { useFlashFlag } from '@/hooks/useFlashFlag';
import QRCode from 'react-qr-code';
import type {
  PaymentsStatusDto,
  StripeConnectBusinessType,
  StripeConnectPanelState,
  StripeConnectPanelCopy,
} from '@signalvote/shared';
import { stripeConnectPanelCopy } from '@signalvote/shared';
import {
  usePaymentsStatus,
  useStartOnboarding,
  useCreateAccountSession,
  useSetDonationSlug,
  usePublishDonationPage,
} from '@/hooks/queries/useStripeConnectPayments';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { useCampaignStore } from '@/stores';
import { APP_ORIGIN, SUBSIDIARY_CODE } from '@/config';
import { getLegalConfig } from '@/config/customerConfig';
import { buildDonationEmbedSnippet } from '@/lib/donationEmbedSnippet';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { DonationDashboard } from './DonationDashboard';
import { AdvancedDonationSettings } from './AdvancedDonationSettings';

// The embedded Connect flow pulls in the heavy `@stripe/connect-js` bundle; keep
// it lazy so only the treasurer who reaches Step 2 loads it, and so tests can
// mock the module without a real Stripe network load.
const EmbeddedOnboarding = lazy(() =>
  import('./EmbeddedOnboarding').then((m) => ({ default: m.EmbeddedOnboarding })),
);

// Fallback brand color if the theme CSS var can't be read (SSR/tests). Matches
// the nonpartisan primary; the live value is read from the active theme below.
const FALLBACK_BRAND = '#2e7d32';

function brandColor(): string {
  if (typeof window === 'undefined') return FALLBACK_BRAND;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary')
    .trim();
  return value || FALLBACK_BRAND;
}

// tone → Badge variant + soft card accent, at the render boundary only.
const TONE_STYLES: Record<
  StripeConnectPanelCopy['tone'],
  { badge: 'default' | 'secondary' | 'destructive' | 'outline'; card: string; label: string }
> = {
  positive: { badge: 'default', card: 'border-green-300 bg-green-50', label: 'Ready' },
  info: { badge: 'secondary', card: 'border-blue-300 bg-blue-50', label: 'In progress' },
  warning: { badge: 'destructive', card: 'border-amber-300 bg-amber-50', label: 'Paused' },
  critical: { badge: 'destructive', card: 'border-red-300 bg-red-50', label: 'Attention' },
};

// Lightly humanize a raw Stripe requirement token for the treasurer.
function humanizeRequirement(token: string): string {
  return token.replace(/[_.]/g, ' ').trim();
}

export function PaymentsOnboardingPage() {
  const { data: status, isLoading, refetch } = usePaymentsStatus();
  const { selectedCampaign } = useCampaignStore();
  // "Finish verification" on the live dashboard re-shows the embedded onboarding
  // flow inline (above the dashboard) without a route change.
  const [verifying, setVerifying] = useState(false);

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  // A demo campaign accepts SIMULATED donations with no Stripe account — the seed publishes the
  // branded page and gifts are written straight to the ledger. Skip the payments-setup wizard
  // entirely (there's nothing to connect) and show the live donation-page controls, so the rep
  // can open the branded page and give as a supporter.
  if (selectedCampaign?.isDemo && status) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-8">
        <header>
          <h1 className="text-2xl font-bold text-foreground">Accept online donations</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your branded donation page is live. In this demo, gifts are simulated straight into your
            ledger — open the page and give as a supporter to watch one arrive in Close the period.
          </p>
        </header>
        <DonationDashboard status={status} onFinishVerification={() => {}} demo />
        <DonationLinkSection status={status} />
      </div>
    );
  }

  // No status yet (query disabled / errored) — treat as not-started.
  const accountExists = status?.accountExists ?? false;
  // Onboarded (matches the ContinueStep gate): charges + details submitted. A
  // payouts-disabled account with charges + details still lands on the dashboard,
  // where the loud health banner flags the problem.
  const onboarded = !!status && status.chargesEnabled && status.detailsSubmitted;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-8">
      {!accountExists ? (
        <StartStep suggestedSlug={status?.suggestedSlug ?? ''} />
      ) : onboarded ? (
        <>
          {verifying && (
            <ContinueStep
              onExit={() => {
                setVerifying(false);
                void refetch();
              }}
            />
          )}
          <DonationDashboard
            status={status!}
            onFinishVerification={() => setVerifying(true)}
          />
          <DonationLinkSection status={status!} />
        </>
      ) : (
        <>
          <ContinueStep onExit={() => void refetch()} />
          <StatusPanel status={status!} />
          <DonationLinkSection status={status!} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — not started. Branded value prop + a prefilled committee summary and
// the business-type / EIN capture, then "Start setup".
// ---------------------------------------------------------------------------

function StartStep({ suggestedSlug }: { suggestedSlug: string }) {
  const { selectedCampaign } = useCampaignStore();
  const { data: financeSettings } = useFinanceSettings();
  const { mutateAsync: startOnboarding, isPending } = useStartOnboarding();

  const [businessType, setBusinessType] =
    useState<StripeConnectBusinessType>('NonProfitUnincorporated');
  const [ein, setEin] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  // The donation page is published (and its link locked) when setup starts, so the treasurer picks
  // the link here. Prefilled with the server's suggestion; sent as the slug (blank → server suggests).
  const [slug, setSlug] = useState(suggestedSlug);
  const [error, setError] = useState<string | null>(null);

  // The payments partner requires a business phone. Collect it here (with an early inline check)
  // rather than letting the treasurer hit an empty required field deep in the embedded flow.
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;

  // Committee (non-profit) types carry an EIN; the individual fallback does not.
  const isCommittee =
    businessType === 'NonProfitUnincorporated' ||
    businessType === 'NonProfitIncorporated';

  const treasurerName = financeSettings?.treasurerName?.trim() || null;
  const committeeName =
    financeSettings?.committeeName?.trim() || selectedCampaign?.name || null;
  const filerId = financeSettings?.stateFilerId?.trim() || null;
  const committeeAddress = [
    financeSettings?.committeeStreet1?.trim(),
    [
      financeSettings?.committeeCity?.trim(),
      financeSettings?.committeeState?.trim(),
    ]
      .filter(Boolean)
      .join(', '),
    financeSettings?.committeeZip?.trim(),
  ]
    .filter(Boolean)
    .join(' ')
    .trim() || null;

  const handleStart = async () => {
    setError(null);
    // Alert on a missing/short phone here, before we ever call the payments partner — so it can't
    // slip through and only surface at their final submit.
    if (!isPhoneValid) {
      setPhoneError('Enter a committee phone number so your payments account can be set up.');
      return;
    }
    setPhoneError(null);
    try {
      await startOnboarding({
        businessType,
        phone: phone.trim(),
        slug: slug.trim() || null,
        ein: isCommittee ? ein.trim() || null : null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start setup');
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">
          Accept online donations
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Give your supporters a simple, secure page to donate online. Funds land
          in your committee&apos;s account, and every gift flows straight into
          your finance records — no manual entry.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your committee</CardTitle>
          <CardDescription>
            We&apos;ll set this up under your committee&apos;s details.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <dl className="grid grid-cols-[8rem_1fr] gap-y-1">
            <dt className="text-muted-foreground">Committee</dt>
            <dd className="text-foreground">{committeeName ?? '—'}</dd>
            {filerId && (
              <>
                <dt className="text-muted-foreground">Filer ID</dt>
                <dd className="text-foreground">{filerId}</dd>
              </>
            )}
            {/* The committee address is READ-ONLY here — prefilled from the
                committee's finance settings (its filing address, which has no
                canonical parallel). There is no free-text edit form to
                autocomplete, so this onboarding step gets no Mapbox address
                capture. The public donor page's address autocomplete (which
                consumes the public-token seam added here) is a separate ticket. */}
            {committeeAddress && (
              <>
                <dt className="text-muted-foreground">Address</dt>
                <dd className="text-foreground">{committeeAddress}</dd>
              </>
            )}
            {treasurerName && (
              <>
                <dt className="text-muted-foreground">Treasurer</dt>
                <dd className="text-foreground">{treasurerName}</dd>
              </>
            )}
          </dl>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <Label className="text-sm font-semibold">Is your committee incorporated?</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <BusinessTypeChoice
            value="NonProfitUnincorporated"
            selected={businessType === 'NonProfitUnincorporated'}
            title="Not incorporated"
            description="Almost all committees — you never filed articles of incorporation with your state."
            onSelect={() => setBusinessType('NonProfitUnincorporated')}
          />
          <BusinessTypeChoice
            value="NonProfitIncorporated"
            selected={businessType === 'NonProfitIncorporated'}
            title="Incorporated"
            description="You formally incorporated the committee with your state (filed articles of incorporation)."
            onSelect={() => setBusinessType('NonProfitIncorporated')}
          />
          <BusinessTypeChoice
            value="Individual"
            selected={businessType === 'Individual'}
            title="Individual"
            description="No separate committee EIN."
            onSelect={() => setBusinessType('Individual')}
          />
        </div>

        {/* Most treasurers won't know this term, and the honest answer is lopsided — say so plainly
            and reassure that it's safe to leave on the default. Kept as one text node so the
            reassurance reads as a single sentence. */}
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            The vast majority of political committees are not incorporated.
          </span>{' '}
          Not sure? Leave it on “Not incorporated” — you can adjust it during setup.
        </p>

        {isCommittee && (
          <div className="space-y-1.5">
            <Label htmlFor="committee-ein" className="text-sm">
              Committee EIN
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                (optional — you can add it during setup)
              </span>
            </Label>
            <Input
              id="committee-ein"
              inputMode="numeric"
              placeholder="12-3456789"
              value={ein}
              onChange={(e) => setEin(e.target.value)}
              className="max-w-xs"
            />
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="committee-phone" className="text-sm">
            Committee phone number
          </Label>
          <Input
            id="committee-phone"
            type="tel"
            inputMode="tel"
            placeholder="(555) 555-5555"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (phoneError) setPhoneError(null);
            }}
            aria-invalid={phoneError ? true : undefined}
            className="max-w-xs"
          />
          {phoneError ? (
            <p className="text-xs text-red-600">{phoneError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Required to set up your payments account.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="start-slug" className="text-sm">
            Your donation page link
          </Label>
          <Input
            id="start-slug"
            data-testid="start-slug-input"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder={suggestedSlug}
            className="max-w-xs"
          />
          <p className="text-xs text-muted-foreground">
            {slug.trim() ? `donate/${slug.trim()}` : 'We’ll suggest one from your committee name.'}{' '}
            This becomes your public donation page and can’t be changed after setup starts.
          </p>
        </div>
      </section>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <Button onClick={handleStart} disabled={isPending}>
          {isPending ? 'Starting…' : 'Start setup'}
        </Button>
        <p className="text-xs text-muted-foreground">
          Powered by our payments partner. Setup takes a few minutes.
        </p>
      </div>
    </div>
  );
}

interface BusinessTypeChoiceProps {
  value: StripeConnectBusinessType;
  selected: boolean;
  title: string;
  description: string;
  onSelect: () => void;
}

function BusinessTypeChoice({
  value,
  selected,
  title,
  description,
  onSelect,
}: BusinessTypeChoiceProps) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer hover:bg-accent/40',
        selected ? 'border-primary bg-primary/5' : 'border-border',
      )}
    >
      <input
        type="radio"
        name="business-type"
        className="h-4 w-4 mt-0.5"
        value={value}
        checked={selected}
        onChange={onSelect}
      />
      <span>
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — in progress. Mounts the embedded Connect onboarding.
// ---------------------------------------------------------------------------

function ContinueStep({ onExit }: { onExit: () => void }) {
  const { mutateAsync: createAccountSession } = useCreateAccountSession();

  return (
    <section className="space-y-3" data-testid="embedded-onboarding-step">
      <header>
        <h2 className="text-lg font-semibold text-foreground">
          Finish setting up your donations
        </h2>
        <p className="text-sm text-muted-foreground">
          Complete the steps below to start accepting gifts. You can leave and
          come back — your progress is saved.
        </p>
      </header>
      <Suspense
        fallback={
          <div className="rounded-lg border border-border bg-muted/30 p-6 text-sm text-muted-foreground">
            Preparing your secure setup…
          </div>
        }
      >
        <EmbeddedOnboarding
          createAccountSession={() => createAccountSession()}
          colorPrimary={brandColor()}
          onExit={onExit}
        />
      </Suspense>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Status panel — renders the treasurer-first panel copy as a colored card and
// lists any currently-due requirements + deadline.
// ---------------------------------------------------------------------------

function StatusPanel({ status }: { status: PaymentsStatusDto }) {
  // panelState is only null before an account exists; this panel renders only
  // when accountExists, so fall back defensively to ActionNeeded.
  const state: StripeConnectPanelState = status.panelState ?? 'ActionNeeded';
  const copy = stripeConnectPanelCopy(state);
  const tone = TONE_STYLES[copy.tone];

  return (
    <Card className={cn('border', tone.card)} data-testid="payments-status-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">{copy.title}</CardTitle>
          <Badge variant={tone.badge}>{tone.label}</Badge>
        </div>
        <CardDescription>{copy.description}</CardDescription>
      </CardHeader>
      {(status.currentlyDueRequirements.length > 0 || status.currentDeadline) && (
        <CardContent className="space-y-2">
          {status.currentlyDueRequirements.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Still needed
              </p>
              <ul className="mt-1 list-disc pl-5 text-sm text-foreground">
                {status.currentlyDueRequirements.map((req) => (
                  <li key={req}>{humanizeRequirement(req)}</li>
                ))}
              </ul>
            </div>
          )}
          {status.currentDeadline && (
            <p className="text-xs text-muted-foreground">
              Due by {status.currentDeadline}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Donation-page link — slug input, save, live preview, publish. Once published
// the slug is locked and the link is copyable.
// ---------------------------------------------------------------------------

function DonationLinkSection({ status }: { status: PaymentsStatusDto }) {
  const { mutateAsync: setSlug, isPending: isSaving } = useSetDonationSlug();
  const { mutateAsync: publish, isPending: isPublishing } = usePublishDonationPage();

  const [slug, setSlugValue] = useState(status.slug ?? status.suggestedSlug ?? '');
  const [error, setError] = useState<string | null>(null);
  const [copied, flashCopied] = useFlashFlag(2000);
  const [embedCopied, flashEmbedCopied] = useFlashFlag(2000);

  const published = status.published;
  const trimmedSlug = slug.trim();
  // Prefer the server-built path; fall back to a locally-built preview.
  const previewPath =
    status.donationPagePath ??
    (trimmedSlug ? `/${SUBSIDIARY_CODE}/donate/${trimmedSlug}` : null);
  // Absolute URL for the share tools (QR payload + open action) — same origin as the dashboard.
  const absoluteUrl = previewPath ? `${APP_ORIGIN}${previewPath}` : null;

  const handleSave = async () => {
    setError(null);
    try {
      await setSlug({ slug: trimmedSlug });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your link');
    }
  };

  const handlePublish = async () => {
    setError(null);
    try {
      await publish();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not publish your donation page');
    }
  };

  const handleCopy = async () => {
    if (!previewPath) return;
    try {
      await navigator.clipboard.writeText(
        `${APP_ORIGIN}${previewPath}`,
      );
      flashCopied();
    } catch {
      // Clipboard can be blocked; the link is visible to copy manually.
    }
  };

  // The copy-paste iframe snippet campaigns drop onto their own site (auto-resizing, wallet-enabled).
  // Brand the iframe id from the current subsidiary (VoteCivic / SignalVote), not a hardcoded name.
  const brand = getLegalConfig(SUBSIDIARY_CODE).companyName.toLowerCase();
  const embedSnippet = trimmedSlug
    ? buildDonationEmbedSnippet({ origin: APP_ORIGIN, slug: trimmedSlug, brand })
    : null;

  const handleCopyEmbed = async () => {
    if (!embedSnippet) return;
    try {
      await navigator.clipboard.writeText(embedSnippet);
      flashEmbedCopied();
    } catch {
      // Clipboard can be blocked; the code is visible to copy manually.
    }
  };

  return (
    <Card data-testid="donation-link-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">Your donation page link</CardTitle>
          {published && <Badge variant="default">Published</Badge>}
        </div>
        <CardDescription>
          {published
            ? 'Your donation page is live. Share this link with supporters.'
            : 'Choose the web address for your donation page, then publish it.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="donation-slug" className="text-sm">
            Link name
          </Label>
          <Input
            id="donation-slug"
            value={slug}
            onChange={(e) => setSlugValue(e.target.value)}
            disabled={published}
            placeholder={status.suggestedSlug}
            data-testid="donation-slug-input"
          />
        </div>

        {previewPath && (
          <p className="text-sm break-all">
            <span className="text-muted-foreground">Preview: </span>
            <span className="font-mono text-foreground" data-testid="donation-page-preview">
              {absoluteUrl ?? previewPath}
            </span>
          </p>
        )}

        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {published ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={handleCopy}>
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              {absoluteUrl && (
                <a
                  href={absoluteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"
                >
                  Open donation page
                </a>
              )}
            </div>
            {absoluteUrl && (
              <div
                data-testid="donation-qr"
                className="inline-block rounded-md border bg-white p-3"
              >
                <QRCode value={absoluteUrl} size={144} />
                <p className="mt-2 max-w-[144px] text-center text-xs text-muted-foreground">
                  Scan to open your donation page.
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              This link is locked now that your page is published.
            </p>

            {embedSnippet && (
              <div className="space-y-2 border-t pt-3" data-testid="donation-embed-section">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">Embed on your website</p>
                    <p className="text-xs text-muted-foreground">
                      Paste this into your site to show the donation form inline. It resizes itself and
                      supports Apple Pay and Google Pay.
                    </p>
                  </div>
                  <Button variant="outline" onClick={handleCopyEmbed}>
                    {embedCopied ? 'Copied' : 'Copy code'}
                  </Button>
                </div>
                <pre
                  data-testid="donation-embed-code"
                  className="max-h-40 overflow-auto rounded-md border bg-muted/40 p-3 text-xs text-foreground"
                >
                  <code>{embedSnippet}</code>
                </pre>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving || !trimmedSlug}
            >
              {isSaving ? 'Saving…' : 'Save link'}
            </Button>
            <Button
              onClick={handlePublish}
              disabled={isPublishing || !status.slug}
            >
              {isPublishing ? 'Publishing…' : 'Publish donation page'}
            </Button>
          </div>
        )}

        {/* Advanced settings (custom amount presets) — collapsed by default, editable whether or not
            the page is published, so the admin can prepare it before going live. */}
        <AdvancedDonationSettings />
      </CardContent>
    </Card>
  );
}
